/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Inject, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import * as _ from 'lodash';
import {catchError, EMPTY, finalize, forkJoin, map, Observable, of, Subject, switchMap} from 'rxjs';
import template from './ad-unit-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {SignalAdminResourcePage} from '../common/controller/signal-admin-resource-api';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {createObjectTypeItemLink} from '../common/directives/adn-select/adn-select-item-link.util';
import {AdUnitCommonsHost, createAdUnitCommons, resetDimensionsBasedOnAdUnitOption} from './ad-unit-commons';
import {AdUnitSizeModalComponent} from './ad-unit-size-modal.component';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {LocalPausing} from '../../components/session/local-pausing';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {getCountryName} from '../common/directives/country-select/countries';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {WINDOW} from '../angular-platform-providers';
import {AdUnitApiService, AdUnitLookupModel, AdUnitModel, AdUnitTimeZone} from './ad-unit-api.service';
import {AdUnitSiteSearchState} from './ad-unit-search-state';

@Component({
  standalone: false,
  selector: 'adn-ad-unit-edit-page',
  template
})
export class AdUnitEditPageComponent extends SignalAdminResourceEditPageBase<AdUnitModel> implements AdUnitCommonsHost {
  readonly fullPageFormats = [
    {code: 'TOP_SCROLL', name: 'Top Scroll'},
    {code: 'MID_SCROLL', name: 'Mid Scroll'},
    {code: 'DOUBLE_FULLSCREEN', name: 'Double Fullscreen'}
  ];

  readonly rulesetItemLink = createObjectTypeItemLink('Ruleset');
  readonly blocklistItemLink = createObjectTypeItemLink('Blocklist');
  readonly siteItemLink = createObjectTypeItemLink('Site');

  readonly siteSearchState: AdUnitSiteSearchState;
  readonly lookupsLoading = signal(true);
  readonly lookupLoadFailed = signal(false);
  networkTimeZone: string;
  isAxPublisher = false;
  canPauseAdUnits = false;
  pureMarketplacePlatform = false;
  isPoliticalAdsPlatform = false;
  showFullPageOptions = false;
  showCountries = false;
  paused = false;
  politicalAdvertising: 'BLOCKED' | 'UNBLOCKED' = 'UNBLOCKED';
  specialDimensions = false;

  timeZones: AdUnitTimeZone[] | undefined;
  specifyTimeZone = false;
  timeZoneChange?: () => void;
  targetingTypes: any[] = [];
  blocklists: AdUnitLookupModel[] = [];
  rulesets: AdUnitLookupModel[] = [];
  siteRulesets: any[] = [];
  targetingRulesets: any[] = [];
  sizeRulesets: any[] = [];
  layoutRulesets: any[] = [];

  readonly externalAdUnitsHook: {
    model: any;
    editsMade: () => void;
    initExtAdUnits?: () => void;
    beforeSubmitExtAdUnit?: () => void;
  };

  private readonly commons: ReturnType<typeof createAdUnitCommons>;
  private readonly adUnitApi = inject(AdUnitApiService);
  private readonly adUnitDestroyRef = inject(DestroyRef);
  private readonly siteSelections = new Subject<string>();

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    @Inject(WINDOW) private readonly window: any,
    private readonly angularModalService: AngularModalService,
    private readonly localPausing: LocalPausing,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(inject(AdUnitApiService), 'adUnit');

    this.networkTimeZone = LocalNetworkProfile.get('timeZone');
    this.isAxPublisher = LocalUserPermissions.getAnyVisibility().AX_PUBLISHER === true;
    this.canPauseAdUnits = this.localPausing.get();
    this.pureMarketplacePlatform = LocalNetworkProfile.isPureMarketplacePlatform();
    this.isPoliticalAdsPlatform = LocalNetworkProfile.isPoliticalAdsPlatform();
    this.showCountries = this.localNetworkInfo.getSiteCountries().length > 1;

    const isSpecialUser = localUserProfile.get('username') === 'adnuntius@adnuntius.com'
      || localUserProfile.get('username') === 'broker1@adnuntius.com';
    const isLocalhost = this.window.location.hostname === 'localhost';
    this.showFullPageOptions = isLocalhost || (this.pureMarketplacePlatform && isSpecialUser);

    this.commons = createAdUnitCommons(this, LocalNetworkProfile, 'editingModel');
    this.commons.setTargetingTypes();
    this.commons.setTimezoneChange();
    this.siteSearchState = new AdUnitSiteSearchState(this.adUnitApi, this.adUnitDestroyRef);

    this.externalAdUnitsHook = {
      model: this.editingModel,
      editsMade: () => this.markExternalFormDirty()
    };

    this.siteSelections.pipe(
      switchMap((siteId) => this.adUnitApi.getSite(siteId).pipe(
        map((site) => ({siteId, site})),
        catchError(() => EMPTY)
      )),
      takeUntilDestroyed(this.adUnitDestroyRef)
    ).subscribe(({siteId, site}) => {
      if (this.editingModel.site?.id !== siteId) {
        return;
      }
      this.editingModel = {...this.editingModel, earningsAccount: site.earningsAccount};
    });

    this.loadLookups();
  }

  get politicalAdvertisingBlocked(): boolean {
    return this.politicalAdvertising === 'BLOCKED';
  }

  set politicalAdvertisingBlocked(blocked: boolean) {
    this.politicalAdvertising = blocked ? 'BLOCKED' : 'UNBLOCKED';
    this.markExternalFormDirty();
  }

  protected override afterEditingModelReset(): void {
    this.externalAdUnitsHook.model = this.editingModel;
    this.specifyTimeZone = !!this.editingModel.timeZone;
    this.runAfterInitAfterSave();
    if (!this.isNew && this.externalAdUnitsHook.initExtAdUnits) {
      this.externalAdUnitsHook.initExtAdUnits();
    }
  }

  protected override prepareForSubmit(model: AdUnitModel): void {
    if (model.politicalAdvertising !== this.politicalAdvertising) {
      if (this.politicalAdvertising === 'BLOCKED') {
        model.politicalAdvertising = 'BLOCKED';
      } else {
        model.politicalAdvertising = 'ALLOWED';
      }
    }
    model.timeZone = this.specifyTimeZone ? model.timeZone : null;
    resetDimensionsBasedOnAdUnitOption(model);

    if (!this.specialDimensions) {
      model.fullPageWidth = 0;
      model.fullPageHeight = 0;
      model.mobileFullPageWidth = 0;
      model.mobileFullPageHeight = 0;
    }

    if (this.externalAdUnitsHook.beforeSubmitExtAdUnit) {
      this.externalAdUnitsHook.model = model;
      this.externalAdUnitsHook.beforeSubmitExtAdUnit();
      this.externalAdUnitsHook.model = this.editingModel;
    }
  }

  addDimensions(): void {
    this.editingModel.dimensions = this.editingModel.dimensions || [];
    this.editingModel.dimensions.push([]);
    this.markExternalFormDirty();
  }

  removeDimensions(index: number): void {
    _.pullAt(this.editingModel.dimensions, index);
    this.markExternalFormDirty();
  }

  previewSize(): void {
    this.angularModalService.show(AdUnitSizeModalComponent, {
      class: 'modal-lg',
      initialState: {
        minWidth: _.isFinite(this.editingModel.minWidth) ? this.editingModel.minWidth : 0,
        minHeight: _.isFinite(this.editingModel.minHeight) ? this.editingModel.minHeight : 0,
        width: _.isFinite(this.editingModel.width) ? this.editingModel.width : 0,
        height: _.isFinite(this.editingModel.height) ? this.editingModel.height : 0
      }
    });
  }

  onTimeZoneModeChange(): void {
    if (this.timeZoneChange) {
      this.timeZoneChange();
    }
    this.markExternalFormDirty();
  }

  onSiteChange(): void {
    this.markExternalFormDirty();
    this.loadSiteRelation();
  }

  private runAfterInitAfterSave(): void {
    this.editingModel.countryExpanded = getCountryName(this.editingModel.country);

    this.loadSiteRelation();

    this.politicalAdvertising = 'BLOCKED';
    if (!this.editingModel.politicalAdvertising || this.editingModel.politicalAdvertising !== 'BLOCKED') {
      this.politicalAdvertising = 'UNBLOCKED';
    }

    this.editingModel.targetingControl = this.editingModel.targetingControl || 'NONE';
    this.editingModel.fullPageFormats = this.editingModel.fullPageFormats || [];
    this.editingModel.allowedTargetTypes = this.editingModel.allowedTargetTypes || [];
    this.editingModel.dimensions = _.isArray(this.editingModel.dimensions) ? this.editingModel.dimensions : [[]];
    this.editingModel.dimensions[0] = _.isArray(this.editingModel.dimensions[0]) ? this.editingModel.dimensions[0] : [];
    this.editingModel.renderOption = this.editingModel.renderOption || null;

    const isFixed = _.get(this.editingModel, ['dimensions', 0, 0]) >= 0
      && _.get(this.editingModel, ['dimensions', 0, 1]) >= 0;
    this.editingModel.adUnitOption = this.editingModel.adUnitOption || (isFixed ? 'FIXED' : 'RANGE');
    this.paused = this.state?.model()?.paused;

    this.specialDimensions = !!(
      this.editingModel.fullPageWidth
      || this.editingModel.fullPageHeight
      || this.editingModel.mobileFullPageWidth
      || this.editingModel.mobileFullPageHeight
    );
    if (this.editingModel.fullPageWidth === 0) {
      this.editingModel.fullPageWidth = undefined;
    }
    if (this.editingModel.fullPageHeight === 0) {
      this.editingModel.fullPageHeight = undefined;
    }
    if (this.editingModel.mobileFullPageWidth === 0) {
      this.editingModel.mobileFullPageWidth = undefined;
    }
    if (this.editingModel.mobileFullPageHeight === 0) {
      this.editingModel.mobileFullPageHeight = undefined;
    }

    this.resolveBlocklistReference();
    this.resolveRulesetReferences();
  }

  private loadLookups(): void {
    this.lookupsLoading.set(true);
    this.lookupLoadFailed.set(false);
    forkJoin({
      blocklists: this.lookupOrEmpty(this.adUnitApi.listBlocklists()),
      rulesets: this.pureMarketplacePlatform
        ? this.lookupOrEmpty(this.adUnitApi.listRulesets())
        : of({results: [], totalCount: 0} as SignalAdminResourcePage<AdUnitLookupModel>),
      timeZones: this.adUnitApi.listTimeZones().pipe(catchError(() => {
        this.lookupLoadFailed.set(true);
        return of([] as AdUnitTimeZone[]);
      }))
    }).pipe(
      finalize(() => this.lookupsLoading.set(false)),
      takeUntilDestroyed(this.adUnitDestroyRef)
    ).subscribe((lookups) => {
      this.blocklists = lookups.blocklists.results || [];
      this.rulesets = lookups.rulesets.results || [];
      this.timeZones = lookups.timeZones;
      this.siteRulesets = _.filter(this.rulesets, (rs: any) => rs.type === 'INVENTORY');
      this.targetingRulesets = _.filter(this.rulesets, (rs: any) => rs.type === 'TARGETING');
      this.sizeRulesets = _.filter(this.rulesets, (rs: any) => rs.type === 'SIZE');
      this.layoutRulesets = _.filter(this.rulesets, (rs: any) => rs.type === 'LAYOUT');
      this.resolveBlocklistReference();
      this.resolveRulesetReferences();
    });
  }

  private lookupOrEmpty(
    source: Observable<SignalAdminResourcePage<AdUnitLookupModel>>
  ): Observable<SignalAdminResourcePage<AdUnitLookupModel>> {
    return source.pipe(catchError(() => {
      this.lookupLoadFailed.set(true);
      return of({results: [], totalCount: 0});
    }));
  }

  private loadSiteRelation(): void {
    const siteId = this.editingModel.site?.id;
    if (siteId) {
      this.siteSelections.next(siteId);
    } else {
      this.editingModel.earningsAccount = null;
    }
  }

  private resolveBlocklistReference(): void {
    if (!this.editingModel?.blocklist?.id) {
      return;
    }
    const match = _.find(this.blocklists, ['id', this.editingModel.blocklist.id]);
    if (match) {
      this.editingModel.blocklist = match;
    }
  }

  private resolveRulesetReferences(): void {
    if (this.editingModel.siteRuleset?.id) {
      const siteRuleset = _.find(this.rulesets, ['id', this.editingModel.siteRuleset.id]);
      if (siteRuleset) {
        this.editingModel.siteRuleset = siteRuleset;
      }
    }
  }
}
